import { getTaskStatus } from './api';
import { TaskStatus, SimulationResults, SimulationProgress, ClusterProgress, SimulationTaskStatus } from '../../types/simulation';
import { AppError, StructuredError } from '../../types/error.d';
import { loggingService } from '../logging/loggingService';

export class SimulationProgressTracker {
  private taskId: string;
  private pollInterval: number = 2000; // 2 seconds
  private maxPollTime: number = 3600000; // 60 minutes
  private apiBaseUrl: string;
  private onProgressUpdate: (progress: SimulationProgress) => void;
  private isCancelled: boolean = false;

  private normalizeTaskStatus(statusValue: unknown): SimulationTaskStatus {
    const normalized = String(statusValue ?? 'pending').toLowerCase();
    if (normalized === 'running' || normalized === 'completed' || normalized === 'failed') {
      return normalized;
    }
    return 'pending';
  }

  constructor(
    taskId: string, 
    apiBaseUrl: string, 
    onProgressUpdate: (progress: SimulationProgress) => void,
    pollInterval: number = 2000,
    maxPollTime: number = 3600000
  ) {
    this.taskId = taskId;
    this.apiBaseUrl = apiBaseUrl;
    this.onProgressUpdate = onProgressUpdate;
    this.pollInterval = pollInterval;
    this.maxPollTime = maxPollTime;
  }

  async trackProgress(): Promise<SimulationResults> {
    let attempts = 0;
    const maxAttempts = Math.floor(this.maxPollTime / this.pollInterval);

    loggingService.debug(`Starting progress tracking for task ${this.taskId}`, { category: 'SimulationProgress' });
    loggingService.debug(`Max attempts: ${maxAttempts}, Poll interval: ${this.pollInterval}ms`, { category: 'SimulationProgress' });

    while (attempts < maxAttempts && !this.isCancelled) {
      try {
        attempts++;
        loggingService.debug(`Polling attempt ${attempts}/${maxAttempts} for task ${this.taskId}`, { category: 'SimulationProgress' });

        const taskStatus = await this.pollStatus();
        const normalizedTaskStatus = this.normalizeTaskStatus(taskStatus.status);
        
        // Update progress
        const progress: SimulationProgress = {
          taskId: this.taskId,
          status: normalizedTaskStatus,
          progress: taskStatus.progress,
          message: taskStatus.message
        };

        this.onProgressUpdate(progress);

        loggingService.debug(`Task status: ${taskStatus.status}, message: ${taskStatus.message}`, { category: 'SimulationProgress' });

        // Check if completed
        loggingService.debug('Progress tracker completion check', {
          category: 'SimulationProgress',
          taskStatus: taskStatus.status,
          isCompleted: normalizedTaskStatus === 'completed',
        });
        
        if (normalizedTaskStatus === 'completed') {
          loggingService.debug(`Task ${this.taskId} completed successfully`, { category: 'SimulationProgress' });
          const canonicalRunId = taskStatus.results_id;

          // Fetch results if available
          if (canonicalRunId) {
            const results: SimulationResults = {
              results_id: canonicalRunId,
              scenario_id: '', // Will be filled by caller
              scenario_name: '', // Will be filled by caller
              status: 'completed',
              summary: {
                assigned_rides: taskStatus.summary?.assigned_rides || 0,
                unassigned_rides: taskStatus.summary?.unassigned_rides || 0,
                total_rides: (taskStatus.summary?.assigned_rides || 0) + (taskStatus.summary?.unassigned_rides || 0),
                total_vehicles: 0, // Will be filled from results
                total_distance: 0,
                total_cost: 0,
                average_wait_time: 0
              }
            };
            
            progress.results = results;
            this.onProgressUpdate(progress);
            
            return results;
          }

          throw new AppError({
            what: 'Simulation Completed Without Run ID',
            why: `Task ${this.taskId} reported completion but did not include a canonical run/results identifier.`,
            howToFix: 'Check the task-status payload and ensure the simulator returns run_id or results_id for completed simulations.',
            category: 'API',
            details: { taskId: this.taskId, taskStatus },
          });
        }

        // Check if failed
        if (normalizedTaskStatus === 'failed') {
          loggingService.error(`Task ${this.taskId} failed`, { category: 'SimulationProgress', message: taskStatus.message });
          // pollStatus already throws AppError, so we just re-throw
          // If the message itself contains an AppError from pollStatus, it's already structured
          throw new AppError({
            what: 'Simulation Task Failed',
            why: taskStatus.message || 'An unknown error occurred during task execution.',
            howToFix: 'Review the simulation configuration and logs. Contact support if the issue persists.',
            category: 'BusinessLogic',
            details: taskStatus
          });
        }

        // Wait before next poll
        await this.sleep(this.pollInterval);

      } catch (error) {
        loggingService.error(`Error polling task ${this.taskId}`, { category: 'SimulationProgress', error });
        this.handleError(error); // Use the new structured handleError
        throw error; // Re-throw the original error after handling
      }
    }

    // Handle timeout
    if (this.isCancelled) {
      throw new AppError({
        what: 'Simulation Cancelled',
        why: `Task ${this.taskId} was cancelled by the user.`,
        howToFix: 'You can restart the simulation at any time.',
        category: 'BusinessLogic'
      });
    }

    this.handleTimeout();
    throw new AppError({
      what: 'Simulation Timed Out',
      why: `Simulation task ${this.taskId} timed out after ${this.maxPollTime / 1000} seconds.`,
      howToFix: 'Try again with a smaller dataset or simpler configuration, or increase the timeout duration.',
      category: 'Network'
    });
  }

  private async pollStatus(): Promise<TaskStatus> {
    try {
      const rawResponse = await getTaskStatus(this.taskId);
      let response = rawResponse;
      if (typeof rawResponse === 'string') {
        loggingService.debug('Response is a string, parsing JSON', { category: 'SimulationProgress' });
        try {
          response = JSON.parse(rawResponse);
        } catch (jsonError) {
          loggingService.warn('Could not parse rawResponse as JSON', { category: 'SimulationProgress', jsonError });
          // If it's a string but not valid JSON, treat it as a plain message
          response = { status: 'failed', message: rawResponse };
        }
      }
      loggingService.debug('Task status response', { category: 'SimulationProgress', response });
      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new AppError({
          what: 'Invalid API Response',
          why: 'The task status API returned an unreadable response.',
          howToFix: 'Check your network connection or contact support.',
          category: 'Network',
          details: rawResponse
        });
      }
      // Handle the actual API response format
      const status = response.status || response.task_status || 'pending';
      const progress = response.progress;
      let message = response.message || response.errors_raised || '';
      const normalizedStatus = this.normalizeTaskStatus(status);
      
      loggingService.debug('Progress tracker status parsing', {
        category: 'SimulationProgress',
        rawStatus: status,
        taskStatus: response.task_status,
        responseStatus: response.status,
      });
      
      // Check for failed status (explicit FAILED or presence of errors_raised)
      const hasErrorsRaised = response.errors_raised && String(response.errors_raised).trim().length > 0;
      const isFailedStatus = normalizedStatus === 'failed' || hasErrorsRaised;

      if (isFailedStatus) {
        const rawMessage = message || response.errors_raised || '';
        const isNoPathsFound = String(rawMessage).includes('No paths found');
        const howToFixBasemap = 'Please double-check that the basemap is available and try again.';
        const whatBasemap = 'Basemap Unavailable';
        const whyBasemap = 'Rides and fleet uploaded successfully (auth is fine), and our checks show rides and fleet are on the same day. The failure happened when calculating travel times between locations—the basemap routing service returned "No paths found". This usually means the basemap is unavailable or the routing server cannot be reached.';

        let structuredError: StructuredError;
        try {
          const backendError = typeof rawMessage === 'string' ? (() => { try { return JSON.parse(rawMessage); } catch { return null; } })() : rawMessage;
          if (backendError?.Exception && backendError?.Traceback) {
            // Specific backend error format detected
            structuredError = {
              what: isNoPathsFound ? whatBasemap : 'Simulation Failed in Backend',
              why: isNoPathsFound ? whyBasemap : `Backend Exception: ${backendError.Exception}. Details: ${backendError.Traceback}`,
              howToFix: isNoPathsFound ? howToFixBasemap : 'This indicates an issue with the simulation logic on the server. Please contact support with the provided details.',
              category: 'API',
              details: backendError
            };
          } else {
            // It was JSON, but not the expected backend error structure
            structuredError = {
              what: isNoPathsFound ? whatBasemap : 'Simulation Failed',
              why: isNoPathsFound ? whyBasemap : (rawMessage || 'Unknown error during task execution. Check backend logs.'),
              howToFix: isNoPathsFound ? howToFixBasemap : 'Review the simulation input and try again. If the issue persists, contact support.',
              category: 'BusinessLogic',
              details: response
            };
          }
        } catch (jsonParseError) {
          // Message was not JSON, treat it as a plain failure message
          structuredError = {
            what: isNoPathsFound ? whatBasemap : 'Simulation Failed',
            why: isNoPathsFound ? whyBasemap : (rawMessage || 'Unknown error during task execution.'),
            howToFix: isNoPathsFound ? howToFixBasemap : 'Review the simulation input and try again. If the issue persists, contact support.',
            category: 'BusinessLogic',
            details: response
          };
        }
        throw new AppError(structuredError);
      }
      
      // Accept run/results identifiers from multiple backend payload shapes.
      const resultsId =
        response.results_id ||
        response.run_id ||
        response.task_result?.results_id ||
        response.task_result?.run_id ||
        (
          response.task_result &&
          (
            typeof response.task_result.assigned_rides === 'number' ||
            typeof response.task_result.unassigned_rides === 'number'
          )
            ? response.task_result.id
            : undefined
        );
      return {
        task_id: response.task_id || this.taskId,
        status: normalizedStatus,
        progress: progress,
        message: message,
        results_id: resultsId,
        cluster_progress: response.cluster_progress as ClusterProgress | undefined,
        summary: {
          assigned_rides: response.task_result?.assigned_rides || 0,
          unassigned_rides: response.task_result?.unassigned_rides || 0,
          total_rides: (response.task_result?.assigned_rides || 0) + (response.task_result?.unassigned_rides || 0),
          reasons: response.task_result?.unassigned_reasons || []
        }
      };
    } catch (error) {
      loggingService.error('Error polling task status', { category: 'SimulationProgress', error });
      if (error instanceof AppError) {
        throw error;
      }
      // Wrap other errors in AppError before re-throwing
      throw new AppError({
        what: 'Failed to Retrieve Task Status',
        why: (error as Error).message || 'An unknown error occurred while polling task status.',
        howToFix: 'Check your network connection and try again. If the problem persists, contact support.',
        category: 'Network',
        details: error
      });
    }
  }

  private handleTimeout(): void {
    loggingService.error(`Task ${this.taskId} timed out after ${this.maxPollTime / 1000} seconds`, { category: 'SimulationProgress' });
    
    // Update progress with a structured AppError for timeout
    const timeoutError = new AppError({
      what: 'Simulation Timed Out',
      why: `Simulation task ${this.taskId} timed out after ${this.maxPollTime / 1000} seconds.`,
      howToFix: 'Try again with a smaller dataset or simpler configuration, or increase the timeout duration.',
      category: 'Network'
    });
    
    const timeoutProgress: SimulationProgress = {
      taskId: this.taskId,
      status: 'failed',
      message: timeoutError.structuredError.what + ': ' + timeoutError.structuredError.why // Use structured message
    };
    
    this.onProgressUpdate(timeoutProgress);
  }

  private handleError(error: unknown): void {
    loggingService.error(`Task ${this.taskId} encountered an error`, { category: 'SimulationProgress', error });
    
    let appErrorToReport: AppError;
    if (error instanceof AppError) {
      appErrorToReport = error;
    } else if (error instanceof Error) {
      appErrorToReport = new AppError({
        what: 'Unexpected Error in Progress Tracker',
        why: error.message,
        howToFix: 'Check console for details or contact support.',
        category: 'Unknown',
        details: error
      });
    } else {
      appErrorToReport = new AppError({
        what: 'Unexpected Error in Progress Tracker',
        why: 'An unknown non-Error object was thrown.',
        howToFix: 'Check console for details or contact support.',
        category: 'Unknown',
        details: error
      });
    }
    
    const errorProgress: SimulationProgress = {
      taskId: this.taskId,
      status: 'failed',
      message: appErrorToReport.structuredError.what + ': ' + appErrorToReport.structuredError.why
    };
    
    this.onProgressUpdate(errorProgress);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  cancel(): void {
    loggingService.debug(`Cancelling progress tracking for task ${this.taskId}`, { category: 'SimulationProgress' });
    this.isCancelled = true;
  }

  // Static helper method for easy usage
  static async track(
    taskId: string,
    apiBaseUrl: string,
    onProgressUpdate: (progress: SimulationProgress) => void,
    pollInterval: number = 2000,
    maxPollTime: number = 3600000
  ): Promise<SimulationResults> {
    const tracker = new SimulationProgressTracker(
      taskId,
      apiBaseUrl,
      onProgressUpdate,
      pollInterval,
      maxPollTime
    );
    return tracker.trackProgress();
  }} 
